const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "U";

const formatMemberSince = (dateValue) => {
  if (!dateValue) return "N/A";
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "N/A";

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPhone = (phone) => {
  if (!phone) return "N/A";
  const phoneText = String(phone);
  return phoneText.startsWith("+") ? phoneText : `+91 ${phoneText}`;
};

const formatCurrencyLimit = (value) => {
  if (value === undefined || value === null || value === "") return "No fixed threshold";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);

  return `Rs. ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount)}`;
};

const colorTokens = [
  { bg: "#E6F1FB", tc: "#0C447C", bc: "#B5D4F4" },
  { bg: "#EAF3DE", tc: "#3B6D11", bc: "#C0DD97" },
  { bg: "#FAEEDA", tc: "#854F0B", bc: "#FAC775" },
  { bg: "#EEEDFE", tc: "#3C3489", bc: "#CECBF6" },
  { bg: "#FAECE7", tc: "#993C1D", bc: "#F5C4B3" },
  { bg: "#E1F5EE", tc: "#0F6E56", bc: "#9FE1CB" },
];

const formatCompactCurrency = (amount = 0) => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value === 0) return "Rs. 0";

  const absValue = Math.abs(value);
  if (absValue >= 10000000) return `Rs. ${(value / 10000000).toFixed(2)} Cr`;
  if (absValue >= 100000) return `Rs. ${(value / 100000).toFixed(2)} L`;
  if (absValue >= 1000) return `Rs. ${(value / 1000).toFixed(2)} K`;

  return `Rs. ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value)}`;
};

const formatTrend = (value = 0) => {
  const trend = Number(value);
  if (!Number.isFinite(trend) || trend === 0) return "No change vs previous period";

  return `${trend > 0 ? "+" : ""}${trend}% vs previous period`;
};

const mapCategoryChips = (categories = [], rfqType) =>
  categories
    .filter((category) => category.rfqType === rfqType)
    .slice(0, 8)
    .map((category, index) => ({
      label: category.subCategoryName || category.categoryName || "Uncategorized",
      count: category.count || 0,
      ...colorTokens[index % colorTokens.length],
    }));

const mergeWithDefaultStats = (dashboardPayload = {}, fallbackStats = defaultProfileStats) => {
  const overview = dashboardPayload.overview || {};
  const periodFilter = dashboardPayload.filters?.period || fallbackStats.filters?.period || {};
  const overviewPeriodLabel = periodFilter.isFiltered ? periodFilter.label : null;
  const supplierStats = dashboardPayload.supplierStats || {};
  const monthlyTrend = dashboardPayload.monthlyTrend || fallbackStats.monthlyTrend;
  const projects = dashboardPayload.projects || [];
  const matCategories = mapCategoryChips(dashboardPayload.categories, "material");
  const conCategories = [
    ...mapCategoryChips(dashboardPayload.categories, "contractor"),
    ...mapCategoryChips(dashboardPayload.categories, "labour"),
  ].slice(0, 8);

  return {
    ...fallbackStats,
    filters: dashboardPayload.filters || fallbackStats.filters,
    navigationTargets: dashboardPayload.navigationTargets || {},
    overviewCards: [
      {
        icon: "rfq",
        label: "Created",
        value: overview.rfqsCreated ?? 0,
        period: overviewPeriodLabel || "All time",
        trend: formatTrend(overview.rfqsCreatedTrendPercent),
        accent: "#8B5CF6",
        bubble: "#F1EAFE",
        navigationKey: "created",
      },
      {
        icon: "progress",
        label: "Open",
        value: overview.openRfqs ?? overview.rfqsInProgress ?? 0,
        period: overviewPeriodLabel || "Current",
        trend: "RFQs currently open",
        accent: "#3B82F6",
        bubble: "#E7F0FF",
        navigationKey: "open",
      },
      {
        icon: "po",
        label: "Pending for Approval",
        value: overview.pendingApprovals ?? 0,
        period: overviewPeriodLabel || "All time",
        trend: "Awaiting approval action",
        accent: "#F59E42",
        bubble: "#FFF0E0",
        navigationKey: "pendingApprovals",
      },
      {
        icon: "progress",
        label: "Rework",
        value: overview.reworkRfqs ?? 0,
        period: overviewPeriodLabel || "Current",
        trend: "RFQs returned for rework",
        accent: "#D97706",
        bubble: "#FEF3C7",
        navigationKey: "rework",
      },
      {
        icon: "contract",
        label: "Closed",
        value: overview.closedRfqs ?? 0,
        period: overviewPeriodLabel || "All time",
        trend: "Awarded requirements",
        accent: "#2FB56B",
        bubble: "#E6F8ED",
        navigationKey: "closed",
      },
      {
        icon: "contract",
        label: "Deleted",
        value: overview.deletedRfqs ?? 0,
        period: overviewPeriodLabel || "All time",
        trend: "Soft-deleted RFQs",
        accent: "#EF4444",
        bubble: "#FEE2E2",
        navigationKey: "deleted",
      },
    ],
    rfq: {
      ...fallbackStats.rfq,
      total: overview.rfqsCreated ?? fallbackStats.rfq.total,
      closed: overview.closedRfqs ?? fallbackStats.rfq.closed,
      open: overview.openRfqs ?? overview.rfqsInProgress ?? fallbackStats.rfq.open,
      pending: overview.pendingApprovals ?? fallbackStats.rfq.pending,
      rework: overview.reworkRfqs ?? fallbackStats.rfq.rework,
      deleted: overview.deletedRfqs ?? fallbackStats.rfq.deleted,
    },
    savings: {
      ...fallbackStats.savings,
      total: formatCompactCurrency(overview.savingsAchieved),
      totalRaw: Number(overview.savingsAchieved || 0),
      benchmarkAmount: formatCompactCurrency(overview.savingsBenchmarkAmount),
      finalizedAmount: formatCompactCurrency(overview.savingsFinalizedAmount),
      budgetBasedRfqs: overview.savingsBudgetBasedRfqs ?? 0,
      l1BasedRfqs: overview.savingsL1BasedRfqs ?? 0,
      savingsRfqCount: overview.savingsRfqCount ?? 0,
      avgQuotes: overview.avgQuotesPerRfq ?? 0,
      vendorParticipation: overview.vendorParticipation ?? 0,
      cycleTimeDays: overview.avgCycleTimeDays ?? 0,
      cycleTimeDelta: "Calculated from RFQ lifecycle",
    },
    score: dashboardPayload.procurementScore || fallbackStats.score,
    streak: dashboardPayload.rfqCreationActivity || dashboardPayload.weeklyActivity || fallbackStats.streak,
    rfqCreationActivity: dashboardPayload.rfqCreationActivity || dashboardPayload.weeklyActivity || fallbackStats.rfqCreationActivity,
    leakage: dashboardPayload.leakage || fallbackStats.leakage,
    leakageYtd: dashboardPayload.leakageYtd || fallbackStats.leakageYtd,
    projectLeakage: dashboardPayload.projectLeakage || fallbackStats.projectLeakage,
    categoryActivity: dashboardPayload.categoryActivity || fallbackStats.categoryActivity,
    monthlyTrend,
    matCategories: matCategories.length ? matCategories : fallbackStats.matCategories,
    conCategories: conCategories.length ? conCategories : fallbackStats.conCategories,
    projects,
    supplierStats,
  };
};

const normalizeUserId = (value) => {
  if (value && typeof value === "object") {
    return value.userId ?? value.id ?? value.managerId ?? value.approverId ?? value.value ?? value._id;
  }
  return value;
};

const idsMatch = (first, second) => {
  const firstId = normalizeUserId(first);
  const secondId = normalizeUserId(second);
  if (firstId === undefined || firstId === null || secondId === undefined || secondId === null) return false;
  return String(firstId) === String(secondId);
};

const buildEmptyRfqCreationActivity = () => {
  const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const emptyDay = (weekIndex, dayName) => ({
    date: "",
    label: "",
    dayName,
    count: 0,
    rfqs: [],
    key: `${weekIndex}-${dayName}`,
  });

  return {
    currentWeek: {
      label: "",
      total: 0,
      days: dayLabels.map((dayName) => emptyDay("current", dayName)),
    },
    heatmap: {
      dayLabels,
      weeks: Array.from({ length: 8 }, (_, weekIndex) => ({
        weekStart: `week-${weekIndex}`,
        monthLabel: "",
        days: dayLabels.map((dayName) => emptyDay(weekIndex, dayName)),
      })),
    },
    summary: {
      mostActiveDay: { dayName: "-", average: 0, total: 0 },
      longestStreak: { start: "", end: "", count: 0 },
      thisWeekCount: 0,
      totalLastEightWeeks: 0,
    },
  };
};

const getUserDetail = (userDirectory = {}, userId) => {
  const normalizedId = normalizeUserId(userId);
  const fallbackUser = userId && typeof userId === "object" ? userId : {};

  return userDirectory[String(normalizedId)] || {
    userId: normalizedId,
    name: fallbackUser.name || fallbackUser.fullName || `User ${normalizedId}`,
    email: fallbackUser.email || "N/A",
    mobile: fallbackUser.mobile || fallbackUser.phone || "N/A",
  };
};

const mapManagers = (managerIds = [], userDirectory = {}) =>
  (managerIds || [])
    .map((managerId) => getUserDetail(userDirectory, managerId))
    .filter((manager) => manager?.userId);

const findCurrentUserApprovalLevelIndex = (approvals = [], currentUserId) => {
  if (!currentUserId) return -1;

  return (approvals || []).findIndex((approval) =>
    (approval?.businessDivisions || []).some((division) =>
      (division?.approvers || []).some((approverId) => idsMatch(approverId, currentUserId)),
    ),
  );
};

const groupApproversByDivision = (businessDivisions = []) => {
  const groupedApprovers = new Map();

  businessDivisions.forEach((division) => {
    division.approvers.forEach((approver) => {
      const approverKey = String(approver.userId);
      const currentGroup = groupedApprovers.get(approverKey) || {
        approver,
        divisions: [],
      };

      if (!currentGroup.divisions.includes(division.division)) {
        currentGroup.divisions.push(division.division);
      }

      groupedApprovers.set(approverKey, currentGroup);
    });
  });

  return Array.from(groupedApprovers.values());
};

const mapApprovalLevels = (approvals = [], userDirectory = {}, currentUserId) => {
  const currentUserApprovalLevelIndex = findCurrentUserApprovalLevelIndex(approvals, currentUserId);

  return (approvals || [])
    .map((approval, index) => ({ approval, index }))
    .filter(({ index }) => currentUserApprovalLevelIndex === -1 || index > currentUserApprovalLevelIndex)
    .map(({ approval, index }) => {
      const businessDivisions = (approval?.businessDivisions || [])
        .map((division) => ({
          division: division?.division || "General",
          approvers: (division?.approvers || [])
            .map((approverId) => getUserDetail(userDirectory, approverId))
            .filter((approver) => approver?.userId),
        }))
        .filter((division) => division.approvers.length > 0);
      const isCategoryBased = Number(approval?.threshold) === 1;

      return {
        level: index + 1,
        threshold: approval?.threshold,
        thresholdLabel: isCategoryBased ? "Category/division based approval" : formatCurrencyLimit(approval?.threshold),
        isCategoryBased,
        businessDivisions,
        approverGroups: groupApproversByDivision(businessDivisions),
      };
    })
    .filter((approval) => approval.businessDivisions.length > 0);
};

export const mapProfileUser = (authUser = {}, businessProfile = {}, userDirectory = {}) => {
  const entity = authUser?.userEntity || authUser?.entity || {};
  const currentUserId = normalizeUserId(authUser);
  const approvals = entity?.approvals || [];
  const currentUserApprovalLevelIndex = findCurrentUserApprovalLevelIndex(approvals, currentUserId);
  const isCurrentUserApprover = currentUserApprovalLevelIndex !== -1;
  const company =
    entity.entityName ||
    entity.directoryName ||
    businessProfile?.businessName ||
    businessProfile?.companyName ||
    "Neevay";

  const name = authUser?.name || authUser?.userProfile?.name || "Neevay User";
  const phone = authUser?.mobile || authUser?.phone || authUser?.userProfile?.mobile || "N/A";
  const email = authUser?.email || authUser?.userProfile?.email || "N/A";
  const designation =
    authUser?.roleInCompany ||
    authUser?.designation ||
    authUser?.IAM_role ||
    "Team member";

  return {
    name,
    initials: getInitials(name),
    role: designation,
    designation,
    company,
    email,
    phone: formatPhone(phone),
    memberSince: formatMemberSince(authUser?.createdAt || authUser?.userProfile?.createdAt || authUser?.createdDate),
    phoneVerified: Boolean(authUser?.isVerified || authUser?.mobileVerified),
    kycDone: Boolean(businessProfile?.isActive || entity?.entityId),
    managers: isCurrentUserApprover ? [] : mapManagers(authUser?.managers || authUser?.userProfile?.managers || [], userDirectory),
    approvalLevels: mapApprovalLevels(approvals, userDirectory, currentUserId),
    isCurrentUserApprover,
    selfApproveLimit: authUser?.selfApproveLimit || "N/A",
    l2ApprovalAbove: authUser?.l2ApprovalAbove || "N/A",
  };
};

export const defaultProfileStats = {
  filters: {
    period: {
      key: "",
      label: "All data",
      from: null,
      to: null,
      isFiltered: false,
    },
  },
  rfq: {
    total: 142,
    closed: 98,
    open: 22,
    pending: 14,
    rework: 0,
    cancelled: 8,
    since: "Jan 2024",
  },
  savings: {
    total: "Rs. 4.2 Cr",
    vsLastYear: "+18%",
    avgQuotes: 4.6,
    platformAvg: 3.2,
    vendorParticipation: 74,
    cycleTimeDays: 6.4,
    cycleTimeDelta: "+1.2d vs Q2",
  },
  score: {
    total: 78,
    rfqFreq: 22,
    savingsRate: 18,
    cycleTime: 16,
    vendorEngagement: 22,
  },
  streak: buildEmptyRfqCreationActivity(),
  rfqCreationActivity: buildEmptyRfqCreationActivity(),
  leakage: {
    total: 0,
    justifiedTotal: 0,
    unjustifiedTotal: 0,
    savingsTotal: 0,
    awardedSpend: 0,
    pct: 0,
    breakdown: [
      { key: "UNJUSTIFIED_NON_L1", label: "Unjustified non-L1 awards", amount: 0, pct: 0, color: "#A32D2D" },
      { key: "JUSTIFIED_PAYMENT_TERMS", label: "Justified variance - payment terms", amount: 0, pct: 0, color: "#FD6600" },
    ],
    topLeakages: [],
  },
  leakageYtd: [],
  projectLeakage: {
    summary: {
      totalLeakage: 0,
      totalSavings: 0,
      projectsWithLeakage: 0,
      avgLeakagePercentOfSavings: 0,
      totalProjects: 0,
    },
    rows: [],
  },
  categoryActivity: {
    typeTotals: [
      { rfqType: "material", label: "Material", count: 0 },
      { rfqType: "contractor", label: "Contractor", count: 0 },
      { rfqType: "labour", label: "Labour", count: 0 },
    ],
    tree: [],
  },
  matCategories: [
    { label: "Cement", count: 28, bg: "#E6F1FB", tc: "#0C447C", bc: "#B5D4F4" },
    { label: "Steel & TMT", count: 21, bg: "#EAF3DE", tc: "#3B6D11", bc: "#C0DD97" },
    { label: "Sand & Aggregates", count: 17, bg: "#FAEEDA", tc: "#854F0B", bc: "#FAC775" },
    { label: "Tiles & Stone", count: 12, bg: "#EEEDFE", tc: "#3C3489", bc: "#CECBF6" },
    { label: "Paints", count: 9, bg: "#FAECE7", tc: "#993C1D", bc: "#F5C4B3" },
    { label: "Waterproofing", count: 6, bg: "#E1F5EE", tc: "#0F6E56", bc: "#9FE1CB" },
  ],
  conCategories: [
    { label: "Civil works", count: 18, bg: "#E6F1FB", tc: "#0C447C", bc: "#B5D4F4" },
    { label: "MEP", count: 14, bg: "#EAF3DE", tc: "#3B6D11", bc: "#C0DD97" },
    { label: "Interior", count: 11, bg: "#EEEDFE", tc: "#3C3489", bc: "#CECBF6" },
    { label: "External works", count: 7, bg: "#FAEEDA", tc: "#854F0B", bc: "#FAC775" },
    { label: "Landscaping", count: 4, bg: "#FAECE7", tc: "#993C1D", bc: "#F5C4B3" },
  ],
  monthlyTrend: {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    material: [8, 11, 9, 14, 13, 17],
    contracting: [4, 6, 5, 8, 7, 10],
  },
};

export const mapDashboardStats = (dashboardPayload = {}) =>
  mergeWithDefaultStats(dashboardPayload, defaultProfileStats);

